import React, { useState } from 'react';
import styled from 'styled-components';
import type { ButtonProps } from './ui/Button/Button';
import { Box, Button } from './ui';
import type { BoxProps } from './ui/Box/Box';
import theme from './theme';
import ArrowRightSvg from '../images/icon-arrow-right.svg';
import AlertRedSvg from '../images/triangle_alert_red.svg';
import AlertYellowSvg from '../images/triangle_alert_yellow.svg';
import NoteSvg from '../images/note-icon.svg';
import LockSvg from '../images/lock.svg';
import DocumentSvg from '../images/document.svg';
import ChevronUpSvg from '../images/chevron-up.svg';
import ShareSvg from '../images/share.svg';

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'>;

export const IconWrapper = styled.div`
  margin-top: 4px;
`;

export const IconArrowRight = (props: ImgProps): React.ReactElement => {
  return <img src={ArrowRightSvg} {...props} />;
};

export const IconArrowRightRound = (props: ImgProps): React.ReactElement => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '20px',
      overflow: 'hidden',
      backgroundColor: 'rgb(197, 205, 217)',
      lineHeight: '20px',
      fontSize: '16px',
      width: '30px',
      height: '30px',
    }}
  >
    <IconArrowRight width={16} height={16} {...props} />
  </div>
);

export const AlertIcon = (props: ImgProps): React.ReactElement => {
  return (
    <img src={AlertRedSvg} width={16} height={16} alt="alert" {...props} />
  );
};

export const WarningAlertIcon = (props: ImgProps): React.ReactElement => {
  return (
    <img src={AlertYellowSvg} width={16} height={16} alt="alert" {...props} />
  );
};

const iconColors = {
  default: theme.colors.text,
  blue: theme.colors.blue.blue_400,
};

type IconColor = keyof typeof iconColors;

type InfoIconProps = BoxProps & {
  color?: IconColor;
};

export const InfoIcon = React.forwardRef<HTMLDivElement, InfoIconProps>(
  (props, ref) => {
    const color: IconColor = props.color ?? 'default';

    return (
      <Box ref={ref} width={16} height={16} {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          fill={iconColors[color]}
        >
          <path
            d="M50 2.361A47.638 47.638 0 0 0 2.361 50 47.638 47.638 0 0 0 50 97.639 47.638 47.638 0 0 0 97.639 50 47.638 47.638 0 0 0 50 2.361zm0 16.74a6.22 6.22 0 0 1 6.22 6.221A6.22 6.22 0 0 1 50 31.543a6.22 6.22 0 0 1-6.22-6.22A6.22 6.22 0 0 1 50 19.101zm0 20.221a5.894 5.894 0 0 1 5.906 5.907v29.763A5.894 5.894 0 0 1 50 80.898a5.894 5.894 0 0 1-5.906-5.906V45.23A5.894 5.894 0 0 1 50 39.322z"
            style={{
              opacity: 1,
              fill: iconColors[color],
              fillOpacity: 1,
              stroke: 'none',
              strokeOpacity: 1,
            }}
          />
        </svg>
      </Box>
    );
  },
);

export const NoteIcon = (props: ImgProps): React.ReactElement => {
  return <img src={NoteSvg} width={16} height={16} alt="note" {...props} />;
};

export const LockIcon = (props: ImgProps): React.ReactElement => {
  return (
    <div
      style={{
        position: 'absolute',
        left: '8px',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <img src={LockSvg} width={16} height={16} alt="lock" {...props} />
    </div>
  );
};

export const DocumentIcon = (props: ImgProps): React.ReactElement => {
  return (
    <img src={DocumentSvg} width={16} height={16} alt="document" {...props} />
  );
};

export const RedCircleIcon = (): React.ReactElement => {
  return (
    <div
      data-testid="red-circle-icon"
      style={{
        display: 'inline-block',
        width: '5px',
        height: '5px',
        borderRadius: '3px',
        marginRight: '2px',
        verticalAlign: 'middle',
        backgroundColor: '#FC2815',
        border: '1px solid #fff',
      }}
    ></div>
  );
};

export const ChevronLeftIcon = (props: ImgProps): React.ReactElement => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: '14px',
        height: '14px',
        position: 'relative',
        borderRadius: '50%',
        transform: 'rotate(-90deg)',
        background: 'rgba(0, 0, 0, 0.3)',
      }}
    >
      <img
        src={ChevronUpSvg}
        width={14}
        height={14}
        alt="chevron left"
        {...props}
      />
    </div>
  );
};

export const ChevronRightIcon = (props: ImgProps): React.ReactElement => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: '14px',
        height: '14px',
        position: 'relative',
        borderRadius: '50%',
        transform: 'rotate(90deg)',
        background: 'rgba(0, 0, 0, 0.3)',
      }}
    >
      <img
        src={ChevronUpSvg}
        width={14}
        height={14}
        alt="chevron right"
        {...props}
      />
    </div>
  );
};

export const ShareIcon = (): React.ReactElement => {
  return <img src={ShareSvg} width={16} height={16} alt="share" />;
};

type ArrowDownIconProps = {
  fillColor?: string;
};

export const ArrowDownIcon = ({
  fillColor,
}: ArrowDownIconProps): React.ReactElement => {
  return (
    <svg height="16px" width="16px" viewBox="0 -8 100 100">
      <path
        fill={fillColor}
        d="M21.364 42.218l24.329 24.329c.026.027.034.065.061.091a5.856 5.856 0 004.17 1.711 5.853 5.853 0 004.17-1.711c.027-.027.034-.064.061-.091l24.329-24.329c2.285-2.285 2.285-6.024 0-8.308s-6.024-2.285-8.308 0L49.923 54.161 29.672 33.91c-2.285-2.285-6.024-2.285-8.308 0s-2.285 6.024 0 8.308z"
      />
    </svg>
  );
};

export const ArrowUpIcon = ({
  fillColor,
}: ArrowDownIconProps): React.ReactElement => {
  return (
    <svg
      height="16px"
      width="16px"
      viewBox="0 0 100 100"
      transform="rotate(180)"
    >
      <path
        fill={fillColor}
        d="M21.364 42.218l24.329 24.329c.026.027.034.065.061.091a5.856 5.856 0 004.17 1.711 5.853 5.853 0 004.17-1.711c.027-.027.034-.064.061-.091l24.329-24.329c2.285-2.285 2.285-6.024 0-8.308s-6.024-2.285-8.308 0L49.923 54.161 29.672 33.91c-2.285-2.285-6.024-2.285-8.308 0s-2.285 6.024 0 8.308z"
      />
    </svg>
  );
};

export const AddButton = ({ onPress }: ButtonProps) => (
  <Button
    title="Add new inquiry"
    onPress={onPress}
    variant="transparent"
    style={{ padding: 0, border: 'none' }}
  >
    <Box display="flex" alignContent="center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24.2 24.2"
        width={20}
        height={20}
      >
        <g stroke="#000" strokeWidth={2.2}>
          <path d="M3 12a9 9 0 1 1 18 0 9 9 0 0 1-18 0Z" />
          <path strokeLinecap="round" d="M8 12h8m-4-4v8" />
        </g>
      </svg>
    </Box>
  </Button>
);

export const EditButton = ({ onPress }: ButtonProps) => (
  <Button
    title="Edit order"
    onPress={onPress}
    variant="transparent"
    style={{ padding: 0, border: 'none', marginRight: '4px' }}
  >
    <Box display="flex" alignContent="center">
      <svg
        stroke="#000"
        fill="none"
        viewBox="0 0 24.2 24.2"
        width={20}
        height={20}
      >
        <g strokeLinecap="round" strokeWidth={2.2}>
          <path
            strokeLinejoin="round"
            d="M18 3.5a1.8 1.8 0 1 1 2.5 2.6l-8.1 8L9 15l.8-3.4 8.1-8ZM16 6l2 2"
          />
          <path d="M12.5 3H12a9 9 0 1 0 9 9v-.5" />
        </g>
      </svg>
    </Box>
  </Button>
);

export const AcceptButton = ({ onPress }: ButtonProps) => (
  <Button
    title="Save changes"
    onPress={onPress}
    variant="transparent"
    style={{ padding: 0, border: 'none' }}
  >
    <Box display="flex" alignContent="center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24.2 24.2"
        width={20}
        height={20}
      >
        <g stroke="#000" strokeWidth={2.2}>
          <path d="M3 12a9 9 0 1 1 18 0 9 9 0 0 1-18 0Z" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m16 9-5 6-3-2.7"
          />
        </g>
      </svg>
    </Box>
  </Button>
);

export const DiscardButton = ({ onPress }: ButtonProps) => (
  <Button
    title="Discard changes"
    onPress={onPress}
    variant="transparent"
    style={{ padding: 0, border: 'none' }}
  >
    <Box display="flex" alignContent="center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24.2 24.2"
        width={20}
        height={20}
      >
        <rect
          width={18}
          height={18}
          x={3}
          y={3}
          stroke="#000"
          strokeWidth={2.2}
          rx={9}
        />
        <path
          stroke="#000"
          strokeLinecap="round"
          strokeWidth={2.2}
          d="m9.2 14.8 5.6-5.6"
        />
      </svg>
    </Box>
  </Button>
);

export const BellIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24.2 24.2"
    width={20}
    height={20}
  >
    <path
      d="M19.38 14.38a2.12 2.12 0 0 1 .62 1.5h0A2.12 2.12 0 0 1 17.88 18H6.12A2.12 2.12 0 0 1 4 15.88h0a2.12 2.12 0 0 1
      .62-1.5L6 13V9a6 6 0 0 1 6-6h0a6 6 0 0 1 6 6v4ZM15 18H9a3 3 0 0 0 3 3h0a3 3 0 0 0 3-3Z"
      style={{
        fill: 'none',
        stroke: '#fff',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: 1.5,
      }}
    />
  </svg>
);

export const CheckIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width={20}
      height={20}
    >
      <path
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 12.611 8.923 17.5 20 6.5"
      />
    </svg>
  );
};

export const CheckedIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width={20}
      height={20}
    >
      <path
        stroke="#6699ff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="m4 14 5 5L20 8M6 8.889 9.077 12 16 5"
      />
    </svg>
  );
};

export const RemoveIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width={20}
      height={20}
    >
      <path
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="m6 6 12 12m0-12L6 18"
      />
    </svg>
  );
};

export const ContextMenuIcon = ({
  onPointerDown,
  size = 22,
}: ButtonProps & { size?: number }) => {
  const [hovered, setHovered] = useState(false);
  const svgSize = size - 4;
  return (
    <Button
      title="Context menu button"
      width={`${size}px`}
      height={`${size}px`}
      backgroundColor="#FFFFFF"
      borderColor="transparent"
      display="flex"
      justifyContent="center"
      alignItems="center"
      onPointerDown={onPointerDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      hover={{ backgroundColor: '#FFFFFF' }}
    >
      <svg
        width={svgSize}
        height={svgSize}
        viewBox="0 0 24 22"
        style={{ pointerEvents: 'none' }}
      >
        <path
          fill={hovered ? '#666' : '#000'}
          d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"
        />
      </svg>
    </Button>
  );
};

export const BookmarkIcon = ({
  filled = false,
  onClick,
  ...props
}: {
  filled?: boolean;
  onClick?: () => void;
} & ButtonProps) => {
  return (
    <Button
      title={filled ? 'Remove from shortlist' : 'Add to shortlist'}
      onPress={onClick}
      variant="transparent"
      style={{ padding: 0, border: 'none', marginRight: '4px' }}
      {...props}
    >
      <Box display="flex" alignContent="center">
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill={filled ? '#000' : 'none'}
          stroke="#000"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </Box>
    </Button>
  );
};

type SparkleIconProps = {
  size?: number;
  color?: string;
};

export const SparkleIcon = ({
  size = 16,
  color = '#FFE552',
}: SparkleIconProps): React.ReactElement => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
    >
      <path
        d="M12 2L13.3015 7.20599C13.5555 8.22185 13.6824 8.72978 13.9469 9.14309C14.1808 9.50868 14.4913 9.81915 14.8569 10.0531C15.2702 10.3176 15.7782 10.4445 16.794 10.6985L22 12L16.794 13.3015C15.7782 13.5555 15.2702 13.6824 14.8569 13.9469C14.4913 14.1808 14.1808 14.4913 13.9469 14.8569C13.6824 15.2702 13.5555 15.7782 13.3015 16.794L12 22L10.6985 16.794C10.4445 15.7782 10.3176 15.2702 10.0531 14.8569C9.81915 14.4913 9.50868 14.1808 9.14309 13.9469C8.72978 13.6824 8.22185 13.5555 7.20599 13.3015L2 12L7.20599 10.6985C8.22185 10.4445 8.72978 10.3176 9.14309 10.0531C9.50868 9.81915 9.81915 9.50868 10.0531 9.14309C10.3176 8.72978 10.4445 8.22185 10.6985 7.20599L12 2Z"
        fill={color}
      />
    </svg>
  );
};

export const SuggestionsIcon = ({
  size = 24,
}: {
  size?: number;
}): React.ReactElement => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    style={{
      width: size,
      height: size,
      minWidth: size,
      minHeight: size,
      borderRadius: '50%',
      backgroundColor: theme.colors.neutral.gray_350,
      lineHeight: 0,
      overflow: 'hidden',
    }}
  >
    <SparkleIcon size={size} />
  </Box>
);

type SuggestionsButtonProps = {
  onPress?: ButtonProps['onPress'];
  size?: number;
  title?: string;
  'data-testid'?: string;
};

export const SuggestionsButton = ({
  onPress,
  size = 24,
  title = 'Get suggestions',
  'data-testid': testId,
}: SuggestionsButtonProps): React.ReactElement => {
  return (
    <Button
      title={title}
      aria-label={title}
      onPress={onPress}
      variant="transparent"
      data-testid={testId}
      style={{
        padding: 0,
        border: 'none',
        lineHeight: 0,
      }}
      size={size}
    >
      <SuggestionsIcon size={size} />
    </Button>
  );
};
